import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import taskDetailJson from '../../demo/fixtures/task-detail.json'
import { useCreativeLibrary } from '../../demo/selectors'
import { demoProducts, useDemoStore } from '../../demo/store'
import { Button, ChipSelectable, CreativeCard, Modal, Skeleton } from '../../ui'
import { creativeCopy } from './copy'
import styles from './AiGenerateDialog.module.css'

type Count = 1 | 2 | 4

interface AiGenerateDialogProps {
  open: boolean
  initialProductId: string
  campaignId?: string
  onClose: () => void
  onToast: (message: string) => void
}

export function AiGenerateDialog({ open, initialProductId, campaignId, onClose, onToast }: AiGenerateDialogProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const [productId, setProductId] = useState(initialProductId)
  const [directions, setDirections] = useState<string[]>(creativeCopy.aiDirections.slice(0, 2))
  const [count, setCount] = useState<Count>(2)
  const [phase, setPhase] = useState<'form' | 'generating' | 'done'>('form')
  const [newIds, setNewIds] = useState<string[]>([])
  const timerRef = useRef<number | undefined>(undefined)
  const allFilter = useMemo(() => ({}), [])
  const library = useCreativeLibrary(allFilter)
  const generated = library.filter((item) => newIds.includes(item.id))
  const addAiCreatives = useDemoStore((state) => state.addAiCreatives)
  const attachCreatives = useDemoStore((state) => state.attachCreatives)

  useEffect(() => () => { if (timerRef.current) window.clearTimeout(timerRef.current) }, [])

  const toggleDirection = (direction: string) => setDirections((current) => current.includes(direction) ? current.filter((item) => item !== direction) : [...current, direction])
  const generate = () => {
    if (!directions.length) return
    setPhase('generating')
    timerRef.current = window.setTimeout(() => {
      const ids = addAiCreatives(productId, directions, count)
      setNewIds(ids)
      setPhase('done')
    }, taskDetailJson.regenerateMs.creatives)
  }
  const returnToLibrary = () => {
    onClose()
    if (location.pathname !== '/creatives') void navigate('/creatives')
  }
  const useNow = () => {
    if (!campaignId) return
    attachCreatives(campaignId, newIds)
    onToast(`已添加 ${newIds.length} 条素材，勾选后即可投放`)
    onClose()
  }

  const footer = phase === 'form' ? (
    <><Button variant="ghost" onClick={onClose}>取消</Button><Button disabled={!directions.length} onClick={generate}>开始生成</Button></>
  ) : phase === 'done' ? (
    <><Button variant="secondary" onClick={returnToLibrary}>回到素材库</Button>{campaignId ? <Button onClick={useNow}>用于当前广告</Button> : null}</>
  ) : null

  return (
    <Modal open={open} title="AI 生成素材" onClose={phase === 'generating' ? () => undefined : onClose} footer={footer}>
      {phase === 'form' ? (
        <div className={styles.form} data-scope="dialog-ai-generate">
          <p className={styles.hint}>选商品与创意方向，AI 直接出素材并自动入库。</p>
          <label className={styles.field}>商品
            <select className={styles.select} value={productId} disabled={Boolean(campaignId)} onChange={(event) => setProductId(event.target.value)}>
              {demoProducts.map((product) => <option key={product.id} value={product.id}>{product.name}</option>)}
            </select>
          </label>
          <fieldset className={styles.field}><legend>创意方向</legend><div className={styles.chips}>{creativeCopy.aiDirections.map((direction) => <ChipSelectable key={direction} selected={directions.includes(direction)} onClick={() => toggleDirection(direction)}>{direction}</ChipSelectable>)}</div></fieldset>
          <fieldset className={styles.field}><legend>生成数量</legend><div className={styles.segment}>{([1, 2, 4] as Count[]).map((value) => <button type="button" key={value} className={count === value ? styles.segmentOn : ''} onClick={() => setCount(value)}>{value}</button>)}</div></fieldset>
        </div>
      ) : (
        <div><p className={`${styles.hint} ${phase === 'done' ? styles.done : ''}`}>{phase === 'done' ? '已入库' : '正在生成素材，请稍候'}</p><div className={styles.results} data-scope={phase === 'generating' ? 'creative-skeleton' : undefined}>
          {phase === 'generating' ? Array.from({ length: count }, (_, index) => <div className={styles.skeletonCard} key={index}><Skeleton height={132} radius={0} /><div className={styles.skeletonBody}><Skeleton lineWidth="80" /><Skeleton lineWidth="40" /></div></div>) : generated.map((creative) => <CreativeCard key={creative.id} name={creative.name} kind={creative.kind} cover={creative.cover} dataUrl={creative.dataUrl} duration={creative.duration} source={creative.source} kol={creative.kol} usedByAds={creative.usedByAds} compliance={creative.compliance} showUsage />)}
        </div></div>
      )}
    </Modal>
  )
}
